// src/services/firestore.js
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  query,
  orderBy,
  getDocs,
  where,
  setDoc,
  getDoc,
  limit
} from 'firebase/firestore';
import { db, appId } from './firebase';

/**
 * Obtiene la referencia de la colección de snippets del usuario
 */
export const getSnippetsCollection = (userId) => {
  const path = `artifacts/${appId}/users/${userId}/snippets`;
  return collection(db, path);
};

/**
 * Suscribirse a cambios en tiempo real de TODOS los snippets del workspace.
 * Sin limit para que el conteo total y la búsqueda funcionen siempre.
 */
export const subscribeToSnippets = (userId, callback) => {
  const snippetsRef = getSnippetsCollection(userId);
  const q = query(snippetsRef, orderBy('createdAt', 'desc'));

  return onSnapshot(q, (snapshot) => {
    const snippets = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(snippets);
  });
};

/**
 * Crear un nuevo snippet
 * Ahora guarda con información del creador para trabajo colaborativo
 */
export const createSnippet = async (userId, snippetData) => {
  // Obtener el workspace del usuario (puede ser su propio ID o el del admin si es colaborador)
  const workspaceId = await getUserWorkspace(userId);
  const snippetsRef = getSnippetsCollection(workspaceId);

  const newSnippet = {
    ...snippetData,
    isPinned: false,
    createdBy: userId, // Guardar quién creó el snippet
    workspaceId: workspaceId, // Guardar a qué workspace pertenece
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  return await addDoc(snippetsRef, newSnippet);
};

/**
 * Actualizar un snippet existente
 * Ahora registra quién hizo la última modificación
 */
export const updateSnippet = async (userId, snippetId, snippetData) => {
  // Obtener el workspace del usuario
  const workspaceId = await getUserWorkspace(userId);
  const path = `artifacts/${appId}/users/${workspaceId}/snippets`;
  const snippetRef = doc(db, path, snippetId);

  const updatedData = {
    ...snippetData,
    lastModifiedBy: userId, // Guardar quién modificó por última vez
    updatedAt: serverTimestamp()
  };

  return await updateDoc(snippetRef, updatedData);
};

/**
 * Eliminar un snippet
 */
export const deleteSnippet = async (userId, snippetId) => {
  // Obtener el workspace del usuario
  const workspaceId = await getUserWorkspace(userId);
  const path = `artifacts/${appId}/users/${workspaceId}/snippets`;
  const snippetRef = doc(db, path, snippetId);

  return await deleteDoc(snippetRef);
};

/**
 * Alternar el estado de fijado de un snippet
 */
export const togglePinSnippet = async (userId, snippetId, isPinned) => {
  // Obtener el workspace del usuario
  const workspaceId = await getUserWorkspace(userId);
  const path = `artifacts/${appId}/users/${workspaceId}/snippets`;
  const snippetRef = doc(db, path, snippetId);

  return await updateDoc(snippetRef, {
    isPinned,
    updatedAt: serverTimestamp()
  });
};

// ============================================
// FUNCIONES PARA GESTIONAR USUARIOS
// ============================================

/**
 * Crear o actualizar documento de usuario con status PENDIENTE
 */
export const createUserWithPendingStatus = async (userId, email) => {
  const userRef = doc(db, 'users', userId);

  await setDoc(userRef, {
    uid: userId,
    email: email,
    status: 'pending',
    registroFecha: serverTimestamp(),
    createdAt: serverTimestamp()
  }, { merge: true });
};

/**
 * Obtener todos los usuarios pendientes
 */
export const getPendingUsers = async () => {
  const usersRef = collection(db, 'users');
  const q = query(usersRef, where('status', '==', 'pending'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    uid: doc.id,
    ...doc.data()
  }));
};

/**
 * Obtener todos los usuarios aprobados (active)
 */
export const getApprovedUsers = async () => {
  const usersRef = collection(db, 'users');
  // Usamos 'active' para coincidir con AdminSettings
  const q = query(usersRef, where('status', '==', 'active'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    uid: doc.id,
    ...doc.data()
  }));
};

/**
 * Obtener usuario por ID
 */
export const getUserById = async (userId) => {
  const userRef = doc(db, 'users', userId);
  const snapshot = await getDocs(query(collection(db, 'users'), where('uid', '==', userId)));

  if (snapshot.empty) return null;

  const userData = snapshot.docs[0].data();
  return {
    uid: snapshot.docs[0].id,
    ...userData
  };
};

/**
 * Aprobar un usuario
 */
export const approveUser = async (userId) => {
  const userRef = doc(db, 'users', userId);

  return await updateDoc(userRef, {
    status: 'active',
    approvalDate: serverTimestamp()
  });
};

/**
 * Rechazar un usuario
 */
export const rejectUser = async (userId) => {
  const userRef = doc(db, 'users', userId);

  return await updateDoc(userRef, {
    status: 'rejected',
    rejectionDate: serverTimestamp()
  });
};

/**
 * Obtener estado del usuario
 */
export const getUserStatus = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) return null;

    return userDoc.data().status;
  } catch (error) {
    console.error('Error al obtener estado del usuario:', error);
    return null;
  }
};

/**
 * Marcar/desmarcar usuario como colaborador
 * Ahora verifica limites del plan antes de agregar
 */
export const toggleCollaborator = async (userId, isCollaborator, targetWorkspaceId = null) => {
  const userRef = doc(db, 'users', userId);

  const ADMIN_EMAIL = 'vcecursos@gmail.com';

  // Buscar el userId del admin (workspace owner)
  let adminUserId = targetWorkspaceId || userId;
  if (isCollaborator && !targetWorkspaceId) {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', ADMIN_EMAIL));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      adminUserId = snapshot.docs[0].id;
    }
  }

  // Si se esta activando como colaborador, verificar limites del plan
  if (isCollaborator) {
    const { checkCanAddUser } = await import('./planService');
    const check = await checkCanAddUser(adminUserId);
    if (!check.allowed) {
      throw new Error(check.reason);
    }

    // Incrementar memberCount (skip if workspace doc doesn't exist)
    try {
      const { incrementMemberCount } = await import('./workspaceService');
      await incrementMemberCount(adminUserId);
    } catch { /* ignore if workspace doc doesn't exist */ }
  } else {
    // Decrementar memberCount
    try {
      const { decrementMemberCount } = await import('./workspaceService');
      await decrementMemberCount(userId);
    } catch { /* ignore si no existe workspace */ }
  }

  const result = await updateDoc(userRef, {
    isCollaborator: isCollaborator,
    role: isCollaborator ? 'collaborator' : 'user',
    workspaceId: isCollaborator ? adminUserId : userId,
    collaboratorSince: isCollaborator ? serverTimestamp() : null,
    updatedAt: serverTimestamp()
  });

  // Sync to Supabase so snippets load correctly for collaborators
  try {
    const { supabase } = await import('../lib/supabase');
    // Check if user exists in Supabase
    const { data: existing } = await supabase
      .from('users')
      .select('id')
      .eq('firebase_id', userId)
      .single();

    if (existing) {
      // Update existing user
      await supabase
        .from('users')
        .update({
          is_collaborator: isCollaborator,
          role: isCollaborator ? 'collaborator' : 'user',
          workspace_id: isCollaborator ? adminUserId : userId,
          collaborator_since: isCollaborator ? new Date().toISOString() : null,
          updated_at: new Date().toISOString(),
        })
        .eq('firebase_id', userId);
    } else {
      // Get user email from Firebase doc
      const userSnap = await getDoc(userRef);
      const userData = userSnap.exists() ? userSnap.data() : {};
      await supabase
        .from('users')
        .insert({
          firebase_id: userId,
          email: userData.email || '',
          display_name: userData.displayName || '',
          is_collaborator: isCollaborator,
          role: isCollaborator ? 'collaborator' : 'user',
          workspace_id: isCollaborator ? adminUserId : userId,
          collaborator_since: isCollaborator ? new Date().toISOString() : null,
          status: 'active',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
    }
  } catch (err) {
    console.warn('Supabase sync warning (non-critical):', err.message);
  }

  return result;
};

// Toggle admin role
export const toggleAdmin = async (userId, makeAdmin) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      role: makeAdmin ? 'admin' : 'collaborator',
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error actualizando rol admin:', error);
    throw error;
  }
};

// Actualizar si es vividor (Vendedor)
export const toggleSeller = async (userId, isSeller) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      isSeller: isSeller
    });
    return true;
  } catch (error) {
    console.error('Error actualizando vendedor:', error);
    throw error;
  }
};

export const toggleInstructor = async (userId, isInstructor) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, { isInstructor });
    return true;
  } catch (error) {
    console.error('Error actualizando instructor:', error);
    throw error;
  }
};

/**
 * Obtener información completa del usuario incluyendo rol
 */
export const getUserInfo = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) return null;

    return {
      uid: userId,
      ...userDoc.data()
    };
  } catch (error) {
    console.error('Error al obtener info del usuario:', error);
    return null;
  }
};

/**
 * Obtener workspaceId del usuario (para saber de dónde cargar snippets)
 */
export const getUserWorkspace = async (userId) => {
  try {
    const userInfo = await getUserInfo(userId);

    if (!userInfo) {
      return userId; // Si no existe el usuario, usa su propio ID
    }

    // Si es colaborador, retorna el workspaceId del admin, sino su propio ID
    return userInfo.workspaceId || userId;
  } catch (error) {
    console.error('Error al obtener workspace:', error);
    return userId;
  }
};

// ============================================
// FUNCIONES PARA GESTIONAR SECCIONES
// ============================================

/**
 * Obtiene la referencia de la colección de secciones del usuario
 */
export const getSectionsCollection = (userId) => {
  const path = `artifacts/${appId}/users/${userId}/sections`;
  return collection(db, path);
};

/**
 * Crear sección predeterminada para un usuario nuevo
 */
export const createDefaultSections = async (userId) => {
  const defaultSections = [
    {
      name: 'MASTERCLASS',
      color: '#9333ea',
      icon: 'FiAward',
      order: 0,
      isDefault: true,
      createdAt: serverTimestamp()
    },
    {
      name: 'CURSOS',
      color: '#3b82f6',
      icon: 'FiBook',
      order: 1,
      isDefault: true,
      createdAt: serverTimestamp()
    },
    {
      name: 'OTROS',
      color: '#10b981',
      icon: 'FiFolder',
      order: 2,
      isDefault: true,
      createdAt: serverTimestamp()
    }
  ];

  const sectionsRef = getSectionsCollection(userId);

  const createPromises = defaultSections.map(section =>
    addDoc(sectionsRef, section)
  );

  return await Promise.all(createPromises);
};

/**
 * Suscribirse a cambios en tiempo real de secciones
 */
export const subscribeToSections = (userId, callback) => {
  const sectionsRef = getSectionsCollection(userId);
  const q = query(sectionsRef, orderBy('order', 'asc'));

  return onSnapshot(q, (snapshot) => {
    const sections = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(sections);
  });
};

/**
 * Crear una nueva sección personalizada
 */
export const createSection = async (userId, sectionData) => {
  const sectionsRef = getSectionsCollection(userId);

  // Obtener el orden más alto actual
  const q = query(sectionsRef, orderBy('order', 'desc'));
  const snapshot = await getDocs(q);
  const maxOrder = snapshot.empty ? 0 : (snapshot.docs[0].data().order || 0);

  const newSection = {
    name: sectionData.name,
    color: sectionData.color || '#6b7280',
    icon: sectionData.icon || 'FiFolder',
    order: maxOrder + 1,
    isDefault: false,
    createdAt: serverTimestamp()
  };

  return await addDoc(sectionsRef, newSection);
};

/**
 * Actualizar una sección existente
 */
export const updateSection = async (userId, sectionId, sectionData) => {
  const path = `artifacts/${appId}/users/${userId}/sections`;
  const sectionRef = doc(db, path, sectionId);

  const updatedData = {
    name: sectionData.name,
    color: sectionData.color,
    icon: sectionData.icon,
    updatedAt: serverTimestamp()
  };

  return await updateDoc(sectionRef, updatedData);
};

/**
 * Eliminar una sección
 */
export const deleteSection = async (userId, sectionId, sectionName) => {
  const path = `artifacts/${appId}/users/${userId}/sections`;
  const sectionRef = doc(db, path, sectionId);

  // Primero, actualizar todos los snippets que usan esta sección a null
  const snippetsRef = getSnippetsCollection(userId);
  const q = query(snippetsRef, where('categorySection', '==', sectionName));
  const snapshot = await getDocs(q);

  const updatePromises = snapshot.docs.map(docSnap => {
    const snippetRef = doc(db, `artifacts/${appId}/users/${userId}/snippets`, docSnap.id);
    return updateDoc(snippetRef, {
      categorySection: null,
      updatedAt: serverTimestamp()
    });
  });

  await Promise.all(updatePromises);

  // Luego eliminar la sección
  return await deleteDoc(sectionRef);
};

/**
 * Obtener todas las secciones del usuario
 */
export const getUserSections = async (userId) => {
  const sectionsRef = getSectionsCollection(userId);
  const q = query(sectionsRef, orderBy('order', 'asc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// ============================================
// FUNCIONES PARA GESTIONAR CATEGORÍAS
// ============================================

/**
 * Mover una categoría a una sección diferente
 * Actualiza todos los snippets que pertenecen a esa categoría con la nueva sección
 */
export const moveCategory = async (userId, categoryName, newSection) => {
  const snippetsRef = getSnippetsCollection(userId);
  const q = query(snippetsRef, where('category', '==', categoryName));
  const snapshot = await getDocs(q);

  // Actualizar todos los snippets de esta categoría
  const updatePromises = snapshot.docs.map(docSnap => {
    const snippetRef = doc(db, `artifacts/${appId}/users/${userId}/snippets`, docSnap.id);
    return updateDoc(snippetRef, {
      categorySection: newSection || null,
      updatedAt: serverTimestamp()
    });
  });

  return await Promise.all(updatePromises);
};

// ============================================
// GESTIÓN DE REGLAS DE COMISIÓN (DYNAMIC RULES)
// ============================================

/**
 * Obtener todas las reglas de comisión
 */
export const getCommissionRules = async () => {
  const rulesRef = collection(db, 'commission_rules');
  const q = query(rulesRef, orderBy('order', 'asc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

/**
 * Crear una nueva regla
 */
export const addCommissionRule = async (ruleData) => {
  const rulesRef = collection(db, 'commission_rules');

  // Obtener último orden
  const q = query(rulesRef, orderBy('order', 'desc'));
  const snapshot = await getDocs(q);
  const maxOrder = snapshot.empty ? 0 : (snapshot.docs[0].data().order || 0);

  return await addDoc(rulesRef, {
    ...ruleData,
    order: maxOrder + 1,
    createdAt: serverTimestamp()
  });
};

/**
 * Actualizar una regla
 */
export const updateCommissionRule = async (ruleId, ruleData) => {
  const ruleRef = doc(db, 'commission_rules', ruleId);
  return await updateDoc(ruleRef, {
    ...ruleData,
    updatedAt: serverTimestamp()
  });
};

/**
 * Eliminar una regla
 */
export const deleteCommissionRule = async (ruleId) => {
  const ruleRef = doc(db, 'commission_rules', ruleId);
  return await deleteDoc(ruleRef);
};

/**
 * Inicializar reglas por defecto (SEED)
 * Retorna true si se crearon, false si ya existían
 */
export const seedCommissionRules = async (defaultRules) => {
  const rulesRef = collection(db, 'commission_rules');
  const snapshot = await getDocs(rulesRef);

  if (!snapshot.empty) return false;

  const batchPromises = defaultRules.map((rule, index) =>
    addDoc(rulesRef, {
      ...rule,
      order: index + 1,
      createdAt: serverTimestamp()
    })
  );

  await Promise.all(batchPromises);
  return true;
};

// ============================================
// LECCIONES APRENDIDAS — IA DE VENTAS (memoria global)
// ============================================

/**
 * Obtener todas las lecciones que el admin le ha enseñado a la IA de ventas.
 * Son globales (aplican a todos los análisis).
 */
export const getSalesLessons = async () => {
  const ref = collection(db, 'sales_ai_lessons');
  const q = query(ref, orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * Guardar una nueva lección.
 * lessonData: { lesson: string, verdict?: 'valid'|'invalid'|'note', context?: string, createdByEmail?: string }
 */
export const addSalesLesson = async (lessonData) => {
  const ref = collection(db, 'sales_ai_lessons');
  return await addDoc(ref, {
    ...lessonData,
    createdAt: serverTimestamp()
  });
};

/**
 * Eliminar una lección.
 */
export const deleteSalesLesson = async (lessonId) => {
  const ref = doc(db, 'sales_ai_lessons', lessonId);
  return await deleteDoc(ref);
};

// ============================================
// SUPER VENDEDOR IA — base de conocimiento (aprende de cada venta)
// ============================================

/**
 * Guardar/actualizar el aprendizaje de una venta (técnicas, objeciones, cierre).
 * Se usa el id de la venta como clave para no duplicar al re-analizar.
 */
export const saveSalesKnowledge = async (saleId, data) => {
  if (!saleId) return;
  const ref = doc(db, 'super_vendedor_knowledge', saleId);
  return await setDoc(ref, { ...data, updatedAt: serverTimestamp() }, { merge: true });
};

/**
 * Obtener todo el conocimiento aprendido (insights de cada venta).
 */
export const getSalesKnowledge = async () => {
  const ref = collection(db, 'super_vendedor_knowledge');
  const snapshot = await getDocs(ref);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

// ============================================
// CONVERSACIONES IA (Super Agente)
// ============================================

export const subscribeToConversations = (userId, callback) => {
  const setup = async () => {
    const workspaceId = await getUserWorkspace(userId);
    const colRef = collection(db, `artifacts/${appId}/users/${workspaceId}/ai_conversations`);
    const q = query(colRef, orderBy('updatedAt', 'desc'));
    return onSnapshot(q, (snapshot) => {
      const convos = snapshot.docs.map(d => ({
        id: d.id,
        title: d.data().title || 'Sin título',
        chatMode: d.data().chatMode || 'gestor',
        updatedAt: d.data().updatedAt,
        messageCount: (d.data().messages || []).length
      }));
      callback(convos);
    });
  };
  let unsubscribe = null;
  const promise = setup().then(unsub => { unsubscribe = unsub; });
  return () => { promise.then(() => { if (unsubscribe) unsubscribe(); }); };
};

export const createConversation = async (userId, title, messages, chatMode = 'gestor') => {
  const workspaceId = await getUserWorkspace(userId);
  const colRef = collection(db, `artifacts/${appId}/users/${workspaceId}/ai_conversations`);
  const docRef = await addDoc(colRef, {
    title,
    messages,
    chatMode,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return docRef;
};

export const getConversation = async (userId, conversationId) => {
  const workspaceId = await getUserWorkspace(userId);
  const docRef = doc(db, `artifacts/${appId}/users/${workspaceId}/ai_conversations`, conversationId);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

export const updateConversation = async (userId, conversationId, data) => {
  const workspaceId = await getUserWorkspace(userId);
  const docRef = doc(db, `artifacts/${appId}/users/${workspaceId}/ai_conversations`, conversationId);
  await updateDoc(docRef, { ...data, updatedAt: serverTimestamp() });
};

export const deleteConversation = async (userId, conversationId) => {
  const workspaceId = await getUserWorkspace(userId);
  const docRef = doc(db, `artifacts/${appId}/users/${workspaceId}/ai_conversations`, conversationId);
  await deleteDoc(docRef);
};

// ============================================
// MEMORIA PERSISTENTE IA
// ============================================

export const subscribeToMemory = (userId, callback) => {
  const setup = async () => {
    const workspaceId = await getUserWorkspace(userId);
    const colRef = collection(db, `artifacts/${appId}/users/${workspaceId}/ai_memory`);
    const q = query(colRef, orderBy('extractedAt', 'desc'));
    return onSnapshot(q, (snapshot) => {
      const memories = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      callback(memories);
    });
  };
  let unsubscribe = null;
  const promise = setup().then(unsub => { unsubscribe = unsub; });
  return () => { promise.then(() => { if (unsubscribe) unsubscribe(); }); };
};

export const addMemoryEntry = async (userId, memoryData) => {
  const workspaceId = await getUserWorkspace(userId);
  const colRef = collection(db, `artifacts/${appId}/users/${workspaceId}/ai_memory`);
  return await addDoc(colRef, {
    ...memoryData,
    extractedAt: serverTimestamp(),
    createdAt: serverTimestamp()
  });
};

export const deleteMemoryEntry = async (userId, memoryId) => {
  const workspaceId = await getUserWorkspace(userId);
  const docRef = doc(db, `artifacts/${appId}/users/${workspaceId}/ai_memory`, memoryId);
  await deleteDoc(docRef);
};

export const clearAllMemory = async (userId) => {
  const workspaceId = await getUserWorkspace(userId);
  const colRef = collection(db, `artifacts/${appId}/users/${workspaceId}/ai_memory`);
  const snapshot = await getDocs(colRef);
  const batch = [];
  snapshot.docs.forEach(d => batch.push(deleteDoc(d.ref)));
  await Promise.all(batch);
};
